// Mobile navigation drawer for the "direct" and "tabs" layouts (snippets/mobile-nav*.liquid).
// Owns open/close, focus trap, background inert, scroll lock, and the direct department panels.
// Tabs behaviour is added on top of this core.
if (!customElements.get('mobile-nav-drawer')) {
  const MOBILE_NAV_STORAGE_KEY = 'mymara-mobile-nav-department';

  class MobileNavDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.activePanel = null;
      this.closeTimer = null;
      this.openFrame = null;
      this.inertedBackground = [];
      this.inertedPanel = [];
      this.header = null;
      this.borderOffset = 0;
      this.mediaQuery = null;

      // Named so tests can tell which document listeners belong to this component.
      this.mobileNavKeydown = this.mobileNavKeydown.bind(this);
      this.mobileNavClickOutside = this.mobileNavClickOutside.bind(this);
      this.mobileNavResize = this.mobileNavResize.bind(this);
      this.mobileNavMediaChange = this.mobileNavMediaChange.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onPanelToggle = this.onPanelToggle.bind(this);
      this.onBackClick = this.onBackClick.bind(this);
      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
    }

    connectedCallback() {
      this.container = this.querySelector('[data-mobile-nav-container]');
      if (!this.container) return;
      this.summary = this.container.querySelector(':scope > summary');
      this.drawer = this.container.querySelector('.mobile-nav');
      this.body = this.container.querySelector('.mobile-nav__body');

      // The native <details> fallback may have been opened before this script upgraded the element.
      // Start from a known closed state instead of adopting half-open markup.
      this.container.removeAttribute('open');
      this.container.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));

      this.summary.addEventListener('click', this.onSummaryClick);
      this.panels = Array.from(this.querySelectorAll('[data-mobile-nav-panel]'));
      this.panels.forEach((details) => details.addEventListener('toggle', this.onPanelToggle));
      this.backButtons = Array.from(this.querySelectorAll('[data-mobile-nav-back]'));
      this.backButtons.forEach((button) => button.addEventListener('click', this.onBackClick));

      this.tablist = this.querySelector('[data-mobile-nav-tablist]');
      this.tabs = this.tablist ? Array.from(this.tablist.querySelectorAll('[role="tab"]')) : [];
      if (this.tabs.length) this.setupTabs();

      document.addEventListener('keydown', this.mobileNavKeydown);
      document.addEventListener('click', this.mobileNavClickOutside, true);
      window.addEventListener('resize', this.mobileNavResize);
      this.mediaQuery = window.matchMedia('(min-width: 1070px)');
      if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener('change', this.mobileNavMediaChange);
      else this.mediaQuery.addListener(this.mobileNavMediaChange);
    }

    disconnectedCallback() {
      this.close({ focusTrigger: false, immediate: true });
      if (!this.container) return;
      this.summary.removeEventListener('click', this.onSummaryClick);
      this.panels.forEach((details) => details.removeEventListener('toggle', this.onPanelToggle));
      this.backButtons.forEach((button) => button.removeEventListener('click', this.onBackClick));
      (this.tabs || []).forEach((button) => {
        button.removeEventListener('click', this.onTabClick);
        button.removeEventListener('keydown', this.onTabKeydown);
      });
      document.removeEventListener('keydown', this.mobileNavKeydown);
      document.removeEventListener('click', this.mobileNavClickOutside, true);
      window.removeEventListener('resize', this.mobileNavResize);
      if (this.mediaQuery) {
        if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener('change', this.mobileNavMediaChange);
        else this.mediaQuery.removeListener(this.mobileNavMediaChange);
        this.mediaQuery = null;
      }
    }

    // Drawer

    open() {
      if (this.isOpen || !this.container) return;
      this.isOpen = true;
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      this.container.classList.remove('is-closing');
      this.container.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');

      this.header = this.closest('.section-header') || document.querySelector('.section-header');
      const wrapper = this.closest('.header-wrapper');
      this.borderOffset = wrapper && wrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
      if (this.header) this.header.classList.add('menu-open');
      this.updateViewportVariables();

      document.body.classList.add('overflow-hidden-tablet');
      this.inertBackground();

      this.openFrame = requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.container.classList.add('menu-opening');
        // The drawer's visibility transition starts on the next frame, and hidden elements can't take focus.
        this.openFrame = requestAnimationFrame(() => {
          if (!this.isOpen) return;
          const target = this.initialFocusTarget();
          if (!target) return;
          target.focus({ preventScroll: true });
          if (document.activeElement !== target) {
            this.closeTimer = setTimeout(() => {
              if (this.isOpen && !this.contains(document.activeElement)) target.focus({ preventScroll: true });
            }, 100);
          }
        });
      });
    }

    close({ focusTrigger = true, immediate = false } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      cancelAnimationFrame(this.openFrame);
      clearTimeout(this.closeTimer);

      if (this.activePanel) this.closePanel(this.activePanel, { focusSummary: false });
      this.panels.forEach((details) => { details.open = false; });
      this.container.classList.remove('menu-opening');

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (immediate || reduceMotion) {
        this.container.classList.remove('is-closing');
        this.container.removeAttribute('open');
      } else {
        this.container.classList.add('is-closing');
        this.closeTimer = setTimeout(() => {
          this.container.removeAttribute('open');
          this.container.classList.remove('is-closing');
        }, 400);
      }

      this.summary.setAttribute('aria-expanded', 'false');
      if (this.header) this.header.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden-tablet');
      this.restoreBackground();
      if (focusTrigger) this.summary.focus({ preventScroll: true });
    }

    initialFocusTarget() {
      const selectedTab = this.querySelector('[role="tab"][aria-selected="true"]');
      if (selectedTab && this.isFocusable(selectedTab)) return selectedTab;
      return this.focusables().find((element) => element !== this.summary) || this.summary;
    }

    updateViewportVariables() {
      const root = document.documentElement;
      if (this.header) {
        root.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
        );
      }
      root.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    inertBackground() {
      this.inertedBackground = [];
      const skip = ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'];
      Array.from(document.body.children).forEach((child) => {
        if (skip.includes(child.tagName) || child.contains(this) || child.inert) return;
        child.inert = true;
        this.inertedBackground.push(child);
      });
    }

    restoreBackground() {
      this.inertedBackground.forEach((element) => { element.inert = false; });
      this.inertedBackground = [];
    }

    // Department panels

    openPanel(details) {
      if (this.activePanel === details) return;
      // A toggle event can arrive after the drawer closed (events are async): keep the panel closed.
      if (!this.isOpen) {
        details.open = false;
        return;
      }
      if (this.activePanel) this.closePanel(this.activePanel, { focusSummary: false });
      this.activePanel = details;
      if (!details.open) details.open = true;
      this.drawer.classList.add('mobile-nav--panel-open');

      const item = details.parentElement;
      const toInert = [];
      if (item && item.parentElement) {
        Array.from(item.parentElement.children).forEach((sibling) => { if (sibling !== item) toInert.push(sibling); });
      }
      toInert.push(details.querySelector(':scope > summary'));
      toInert.push(this.querySelector('.menu-drawer__utility-links'));
      this.inertedPanel = toInert.filter((element) => element && !element.inert);
      this.inertedPanel.forEach((element) => { element.inert = true; });

      if (this.body) this.body.scrollTop = 0;
      const back = details.querySelector('[data-mobile-nav-back]');
      if (back) back.focus({ preventScroll: true });
    }

    closePanel(details, { focusSummary = true } = {}) {
      if (details.open) details.open = false;
      if (this.activePanel !== details) return;
      this.activePanel = null;
      this.inertedPanel.forEach((element) => { element.inert = false; });
      this.inertedPanel = [];
      this.drawer.classList.remove('mobile-nav--panel-open');
      if (focusSummary) {
        const summary = details.querySelector(':scope > summary');
        if (summary) summary.focus({ preventScroll: true });
      }
    }

    // Department tabs

    setupTabs() {
      this.tabs.forEach((button) => {
        button.addEventListener('click', this.onTabClick);
        button.addEventListener('keydown', this.onTabKeydown);
      });
      this.tablist.hidden = false;
      let stored = null;
      try {
        stored = window.sessionStorage.getItem(MOBILE_NAV_STORAGE_KEY);
      } catch (error) {
        stored = null;
      }
      const match = this.tabs.find((button) => button.dataset.department === stored);
      this.selectDepartment(match ? match.dataset.department : this.tabs[0].dataset.department);
    }

    selectDepartment(handle, { focus = false, remember = false } = {}) {
      const selected = this.tabs.find((button) => button.dataset.department === handle);
      if (!selected) return;
      this.tabs.forEach((button) => {
        const active = button === selected;
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[role="tabpanel"][data-department]').forEach((panel) => {
        panel.hidden = panel.dataset.department !== handle;
      });
      if (remember) {
        try {
          window.sessionStorage.setItem(MOBILE_NAV_STORAGE_KEY, handle);
        } catch (error) {
          // Storage can be blocked; the selection just won't persist.
        }
      }
      if (focus) selected.focus({ preventScroll: true });
    }

    onTabClick(event) {
      this.selectDepartment(event.currentTarget.dataset.department, { remember: true });
    }

    onTabKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const index = this.tabs.indexOf(event.currentTarget);
      const last = this.tabs.length - 1;
      let next;
      if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;
      event.preventDefault();
      this.selectDepartment(this.tabs[next].dataset.department, { focus: true, remember: true });
    }

    // Focus

    isFocusable(element) {
      if (element.disabled || element.closest('[inert]')) return false;
      // Content of a closed <details> has client rects in Chrome (content-visibility) but isn't tabbable.
      let node = element;
      let details = node.parentElement && node.parentElement.closest('details');
      while (details) {
        const isOwnSummary = node === element && element.tagName === 'SUMMARY' && element.parentElement === details;
        if (!details.open && !isOwnSummary) return false;
        node = details;
        details = node.parentElement && node.parentElement.closest('details');
      }
      if (element.getClientRects().length === 0) return false;
      return getComputedStyle(element).visibility !== 'hidden';
    }

    focusables() {
      if (!this.container) return [];
      const selector = [
        'a[href]', 'button', 'summary', 'input:not([type="hidden"])', 'select', 'textarea',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      const inside = Array.from((this.drawer || this.container).querySelectorAll(selector))
        .filter((element) => element.getAttribute('tabindex') !== '-1' && this.isFocusable(element));
      return [this.summary, ...inside].filter(Boolean);
    }

    // Handlers

    onSummaryClick(event) {
      event.preventDefault();
      if (this.isOpen) this.close();
      else this.open();
    }

    onPanelToggle(event) {
      const details = event.currentTarget;
      if (details.open) this.openPanel(details);
      else this.closePanel(details);
    }

    onBackClick(event) {
      const details = event.currentTarget.closest('[data-mobile-nav-panel]');
      if (details) this.closePanel(details);
    }

    mobileNavKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        if (event.defaultPrevented) return;
        // localization-form closes its own open list on keyup (innermost first), so leave this Escape to it.
        const localization = event.target.closest && event.target.closest('localization-form');
        if (localization && localization.querySelector('[aria-expanded="true"]')) return;
        event.preventDefault();
        if (this.activePanel) this.closePanel(this.activePanel);
        else this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = this.focusables();
      if (items.length === 0) return;
      const index = items.indexOf(document.activeElement);
      const last = items.length - 1;
      if (event.shiftKey) {
        if (index <= 0) {
          event.preventDefault();
          items[last].focus();
        }
      } else if (index === -1 || index === last) {
        event.preventDefault();
        items[0].focus();
      }
    }

    mobileNavClickOutside(event) {
      if (!this.isOpen || this.contains(event.target)) return;
      this.close({ focusTrigger: false });
    }

    mobileNavResize() {
      if (this.isOpen) this.updateViewportVariables();
    }

    mobileNavMediaChange(event) {
      if (event.matches) this.close({ focusTrigger: false, immediate: true });
    }
  }

  customElements.define('mobile-nav-drawer', MobileNavDrawer);
}
